import { api, getHeaders } from '@/api/client';
import type { ApiResponse } from '@/api/schema/common';
import { toSearchParams, validateResponse } from '@/api/utils';

import type { ApprovalsCriteria, FleetJobsCriteria, SetupCalendarRequest, SetupRunningHoursRequest } from './endpoints';
import {
  approvalDashboardItemArrayApiResponseSchema,
  fleetJobInstanceArrayApiResponseSchema,
  type ApprovalDashboardItem,
  type FleetJobInstance
} from './schema';

const BASE_URL = '/pms/api/vesselJobs';

export const getFleetJobInstances = async (criteria?: FleetJobsCriteria): Promise<FleetJobInstance[]> => {
  const query = toSearchParams(criteria);
  const url = query ? `${BASE_URL}/jobs?${query}` : `${BASE_URL}/jobs`;
  const response = await api.get<ApiResponse<FleetJobInstance[]>>(url, getHeaders());
  const parsed = validateResponse(fleetJobInstanceArrayApiResponseSchema, response.data);
  return parsed.Data;
};

export interface FleetJobsKpiCategory {
  Count: number;
  Percentage: number;
}

export interface FleetJobsKpiResult {
  TotalCount: number;
  Overdue: FleetJobsKpiCategory;
  DueSoon: FleetJobsKpiCategory;
  OnTrack: FleetJobsKpiCategory;
  Unknown: FleetJobsKpiCategory;
}

export const getFleetJobsKpi = async (criteria?: FleetJobsCriteria): Promise<FleetJobsKpiResult> => {
  const query = toSearchParams(criteria);
  const url = query ? `${BASE_URL}/kpi?${query}` : `${BASE_URL}/kpi`;
  const response = await api.get<ApiResponse<FleetJobsKpiResult>>(url, getHeaders());
  return response.data.Data;
};

export const setupRunningHours = async (jobId: string, request: SetupRunningHoursRequest): Promise<void> => {
  await api.post<ApiResponse<string>>(`${BASE_URL}/${jobId}/setupRunningHours`, request, getHeaders());
};

export const setupCalendar = async (jobId: string, request: SetupCalendarRequest): Promise<void> => {
  await api.post<ApiResponse<string>>(`${BASE_URL}/${jobId}/setupCalendar`, request, getHeaders());
};

export interface CompleteJobRequest {
  VesselJobInstances: {
    VesselJobInstanceId: string;
    CompletionRunningHours: number | null;
  }[];
  CompletionDate: string;
  ClosingComments: string;
  ConditionParameterName?: string;
  ConditionReading?: number;
  ConditionUnit?: string;
}

export const completeJob = async (request: CompleteJobRequest): Promise<void> => {
  await api.post<ApiResponse<string>>(`${BASE_URL}/completeJob`, request, getHeaders());
};

export const getApprovals = async (criteria?: ApprovalsCriteria): Promise<ApprovalDashboardItem[]> => {
  const query = toSearchParams(criteria);
  const url = query ? `${BASE_URL}/approvals?${query}` : `${BASE_URL}/approvals`;
  const response = await api.get<ApiResponse<ApprovalDashboardItem[]>>(url, getHeaders());
  const parsed = validateResponse(approvalDashboardItemArrayApiResponseSchema, response.data);
  return parsed.Data;
};
